import React, { useState } from "react";
import clsx from "clsx";
import { Tooltip } from "@material-ui/core";

import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SortInactiveIcon from "../../../img/sort-inactive.svg";
import SortAscIcon from "../../../img/sort-asc.svg";
import SortDescIcon from "../../../img/sort-desc.svg";

import PercentageBar from "../../Shared/Graph/components/PercentageBar";
import "../styles/LeaderboardTableRow.scss";
import {
  getLeaderboardLabelByHierarchy,
  getSortFieldLabel,
  getSortOrderLabel,
} from "../utils/kpiDashboardHelper";
import { formatToCommaString } from "../../RevampedDashboard/helper/format";
import PopperMenu from "../../Shared/PopperMenu/PopperMenu";

const LeaderboardTableRow = ({
  variant = "data",
  sortField,
  sortDir,
  handleSort,
  row,
  rowIndex,
  focusedRowIndex,
  setFocusedRowIndex,
  onEmployeeMenuClick,
  isEmployeeDeepDiveOpen = false,
  hierarchy = null,
  showAdditionalColumns = false,
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOptions = [
    {
      label: "View All KPI Parameters",
      onClick: () => {
        setFocusedRowIndex(null);
        onEmployeeMenuClick(row.user_id, "kpi-parameters");
      },
    },
    {
      label: "View Employee Profile",
      onClick: () => {
        setFocusedRowIndex(null);
        onEmployeeMenuClick(row.user_id, "user-profile");
      },
    },
    {
      label: "View Performance Graph",
      onClick: () => {
        setFocusedRowIndex(null);
        onEmployeeMenuClick(row.user_id, "performance-graph");
      },
    },
  ];

  const SortIcon = ({ field }) => (
    <img
      src={
        sortField === field && sortDir
          ? sortDir === "asc"
            ? SortAscIcon
            : SortDescIcon
          : SortInactiveIcon
      }
      alt="Sort Icon"
      className="sort-icon"
    />
  );

  const handleRowMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
    setFocusedRowIndex(rowIndex);
  };

  if (variant === "loading") {
    return (
      <div className="data-row loading-row">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="cell"
            style={{
              flex: 1,
              height: "25px",
              backgroundColor: "#E0E0E0",
              borderRadius: "4px",
              margin: "0 4px",
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === "header") {
    const sortFieldLabel = getSortFieldLabel(sortField);
    const sortOrderLabel = getSortOrderLabel(sortDir);
    const showSortInfo = !!sortFieldLabel && !!sortOrderLabel;

    return (
      <div className="header-row">
        <div className="header-cell sortable-header-cell hierarchy-header">
          {getLeaderboardLabelByHierarchy(hierarchy)}
        </div>
        {showAdditionalColumns && (
          <>
            <div
              className="header-cell sortable-header-cell header-target"
              onClick={() => handleSort("target")}
            >
              <SortIcon field="target" />
              Target
            </div>
            <div
              className="header-cell sortable-header-cell header-ach"
              onClick={() => handleSort("achievement")}
            >
              <SortIcon field="achievement" />
              Achievement
            </div>
          </>
        )}
        <div
          className="header-cell sortable-header-cell ach-pct-header"
          onClick={() => handleSort("ach_pct")}
        >
          <SortIcon field="ach_pct" />
          Ach %
        </div>
        <div
          className="header-cell sortable-header-cell growth-header"
          onClick={() => handleSort("growth_pct")}
        >
          <SortIcon field="growth_pct" />
          Growth %
        </div>
        <div className="header-cell bar-header">Achievement Bar</div>
        <div className={clsx("menu-cell", showSortInfo && "sort-info-cell")}>
          {showSortInfo && (
            <span className="sort-info-text">
              {sortFieldLabel} - {sortOrderLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="data-row">
      <div className="cell hierarchy-cell">
        <span className="hierarchy-rank">{row.rank}</span>
        <div className="hierarchy-user">
          <span className="hierarchy-name">{row.user_name}</span>
          <span className="hierarchy-meta" title={row.user_hierarchy}>
            {row.user_hierarchy}
          </span>
        </div>
      </div>
      {showAdditionalColumns && (
        <>
          <Tooltip
            placement="top"
            title={formatToCommaString(row?.target, "en-US")}
          >
            <div className="cell data-target sortable-data">
              {formatToCommaString(row?.target, "en-US")}
            </div>
          </Tooltip>
          <Tooltip
            placement="top"
            title={formatToCommaString(row?.achievement, "en-US")}
          >
            <div className="cell data-ach sortable-data">
              {formatToCommaString(row?.achievement, "en-US")}
            </div>
          </Tooltip>
        </>
      )}
      <div className="cell ach-pct-cell sortable-data">
        {(row.ach_pct ?? row.achPct ?? 0).toFixed(2)}%
      </div>
      <div
        className={clsx(
          "growth-cell sortable-data",
          (row.growth_pct ?? row.growth ?? 0) > 0
            ? "positive"
            : (row.growth_pct ?? row.growth ?? 0) < 0
              ? "negative"
              : ""
        )}
      >
        {(row.growth_pct ?? row.growth ?? 0) === 0 ? (
          <TrendingFlatIcon className="trending-icon" />
        ) : (row.growth_pct ?? row.growth ?? 0) < 0 ? (
          <TrendingDownIcon className="trending-icon" />
        ) : (
          <TrendingUpIcon className="trending-icon" />
        )}
        {(row.growth_pct ?? row.growth ?? 0).toFixed(2)}%
      </div>
      <div className="bar-cell">
        <PercentageBar percentage={row.ach_pct ?? row.achPct ?? 0} />
      </div>
      <div className="row-menu" onClick={handleRowMenuClick}>
        <MoreVertIcon className="row-menu-icon" />
        {focusedRowIndex === rowIndex && (
          <PopperMenu
            anchorElement={menuAnchorEl}
            className="kpi-parameter-breakdown-menu"
            onRequestClose={() => {
              if (!isEmployeeDeepDiveOpen) {
                setFocusedRowIndex(null);
              }
            }}
            menuItems={menuOptions}
          />
        )}
      </div>
    </div>
  );
};

export default LeaderboardTableRow;
